import {
  getAllArticles,
  getBlogArticle,
  getBlogArticlePreview,
  getBlogArticles
} from "../../lib/blog-articles";
import { getSEOPageProps } from "../../lib/seo";
import config from "../../config";

import Article from "../../components/article";
import NextStory from "../../components/next-story";
import SeoMeta from "../../components/seo-meta";

export default function NewsStoriesPage({ article, otherArticles, seo }) {
  return (
    <>
      <SeoMeta {...seo} />

      <Article article={article} />

      <div className="container">
        <div className="my-40">
          <NextStory articles={otherArticles.items} />
        </div>
      </div>
    </>
  );
}


// 82 articles. Only the most recent are prerendered — the archive gets built on
// first request by `fallback: "blocking"` and then cached like any other page, so
// a deploy never pays for articles nobody reads.
const PRERENDERED_ARTICLES = 12;

export async function getStaticPaths() {
  const articles = await getAllArticles();

  const recent = (articles || [])
    .slice()
    .sort((a, b) => new Date(b.date) - new Date(a.date))
    .slice(0, PRERENDERED_ARTICLES);

  return {
    paths: recent.map((article) => ({ params: { slug: article.slug } })),
    fallback: "blocking"
  };
}

export async function getStaticProps({ params, preview = false, previewData }) {
  try {
    // Preview mode bypasses the static cache, so this runs per request.
    if (preview && previewData?.preview) {
      try {
        const previewArticle = await getBlogArticlePreview(previewData.preview);
        return {
          props: {
            article: previewArticle,
            otherArticles: { items: previewArticle.nextStory ? [previewArticle.nextStory] : [] },
            seo: {}
          }
        };
      } catch (e) {
        console.error("ERROR", e.message);
        return { notFound: true, revalidate: config.NOT_FOUND_TTL };
      }
    }

    const [article, otherArticles, seo] = await Promise.all([
      getBlogArticle(params.slug),
      getBlogArticles({ limit: 3, skip: 0 }), // take the latest 3 articles
      getSEOPageProps({ resolvedUrl: `/news/${params.slug}` })
    ]);

    if (!article || !otherArticles) {
      return { notFound: true, revalidate: config.NOT_FOUND_TTL };
    }

    // filter out the current article, if it exists
    otherArticles.items = otherArticles.items.filter(
      (item) => item.slug !== params.slug
    );

    // slice two more articles
    otherArticles.items = otherArticles.items.slice(0, 2);

    // Push the related next story into the other article's list.
    if (article.nextStory) {
      otherArticles.items.push(article.nextStory);
    }

    return {
      props: {
        article,
        otherArticles,
        seo
      },
      revalidate: config.ISR_TIMING
    };
  } catch (e) {
    console.log(e.message);
    return { notFound: true, revalidate: config.NOT_FOUND_TTL };
  }
}
